import { Registry } from "generated/models";
import _ from "lodash";
import React, { ChangeEvent } from "react";
import Button from "react-bootstrap/Button";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import {
    CHART_ERROR_MESSAGE,
    CHART_NAME_PATTERN,
    CreateChartState,
    useCreateDashboardStore,
} from ".";
import CreateChartModal from "./modal/CreateChart";
import DashboardCreateSelectGatewayModal from "./modal/SelectGateways";

export enum GatewayChartFor {
    Storage = "Storage Usage",
    Status = "Online/Offline Status",
}

export enum GateWayChartType {
    Donut = "Donut Chart",
    Pie = "Pie Chart",
}

type FormData = Record<string, any>;

function formDataFromGateway(gateway: Registry): FormData {
    return Object.keys(GatewayChartFor).reduce((acc, key) => {
        const identifier = `${gateway.gateway_id}.${key}`;
        return {
            ...acc,
            [`${identifier}.chart_name`]: gateway.name + " - " + key,
            [`${identifier}.gateway_name`]: gateway.name,
            [`${identifier}.chart_for`]: true,
            [`${identifier}.chart_type`]: "Donut",
        };
    }, {});
}

function updateFormData(
    currentFormData: FormData,
    selectedGateways: Record<string, Registry>
): FormData {
    const gateways = Object.values(selectedGateways);
    const selectedGatewayIds = gateways.map((gateway) => gateway.gateway_id);

    // Only keep form data of selected gateways
    const entries = Object.entries(currentFormData).filter((entry) =>
        selectedGatewayIds.reduce(
            (acc, currentValue) => acc || entry[0].startsWith(currentValue),
            false
        )
    );

    const currentGatewayIds = _.uniq(
        Object.keys(currentFormData).map((key) => key.split(".")[0])
    );
    const toAdd = _.difference(selectedGatewayIds, currentGatewayIds);
    const gatewaysToBeAdded = gateways.filter((gateway) =>
        toAdd.includes(gateway.gateway_id)
    );

    for (const gateway of gatewaysToBeAdded) {
        entries.push(...Object.entries(formDataFromGateway(gateway)));
    }

    return Object.fromEntries(entries);
}

const GatewayChartCreateData: CreateChartState = {
    title: "Create Gateway Charts...",
    data: [],
    columns: [
        {
            key: "chart_name",
            name: "Chart Name",
        },
        {
            key: "gateway_name",
            name: "Gateway",
        },
        {
            key: "chart_for",
            name: "Chart for",
            render: (data: any) => (
                <>
                    {
                        GatewayChartFor[
                            data.chart_for.capitalize() as keyof typeof GatewayChartFor
                        ]
                    }
                </>
            ),
        },
        {
            key: "chart_type",
            name: "Chart Type",
        },
    ],
    transform: (data: any) => ({
        attributes: [
            {
                type_: data.chart_for,
                gateway_id: data.gateway_id,
            },
        ],
        source: "GATEWAY",
        name: data.chart_name,
        chart: String(data.chart_type).toUpperCase(),
    }),
    validate() {
        return this.data.map((item) => ({
            path: `[name="${item.gateway_id}.${item.chart_for}.chart_name"]`,
            isValid: CHART_NAME_PATTERN.test(item.chart_name.trim()),
            message: CHART_ERROR_MESSAGE,
        }));
    },
};

const DashboardCreateGatewayComponent: React.FunctionComponent = () => {
    const [showSelectModal, setShowSelectModal] = React.useState(false);
    const [selectedGateways, setSelectedGateways] = React.useState<
        Record<string, Registry>
    >({});
    const [formValues, setFormValues] = React.useState<{
        [key: string]: any;
    }>({});
    const { createModalShow, setCreateModalShow, createData } =
        useCreateDashboardStore();

    React.useEffect(() => {
        const createData: any[] = [];
        setCreateModalShow(false);

        const formValuesObject = {} as any;

        for (const key in formValues) {
            _.set(formValuesObject, String(key), formValues[key]);
        }

        for (const gateway_id in formValuesObject) {
            const chartDataList = formValuesObject[gateway_id];
            for (let chartFor in chartDataList) {
                if (GatewayChartFor.hasOwnProperty(chartFor)) {
                    const chartData = chartDataList[chartFor];
                    chartData.chart_for &&
                        createData.push({
                            ...chartDataList[chartFor],
                            chart_for: String(chartFor).toUpperCase(),
                            gateway_id,
                        });
                }
            }
        }

        useCreateDashboardStore.setState({
            createData: {
                ...GatewayChartCreateData,
                data: createData,
            },
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formValues]);

    const onGatewayChange = (event: any, data: Record<string, Registry>) => {
        useCreateDashboardStore.setState({
            createData: undefined,
        });

        setFormValues(updateFormData(formValues, data));
        setSelectedGateways(data);
    };

    const formChange = (e: ChangeEvent<HTMLFormElement>) => {
        const { name, value, checked, type } = e.target;

        if (["checkbox"].includes(type)) {
            _.set(formValues, name, checked);
        } else {
            _.set(formValues, name, value);
        }

        setFormValues({ ...formValues });
    };

    const onRemove = (gateway: any) => {
        const data = _.omit(selectedGateways, gateway.gateway_id);
        onGatewayChange(null, data);
    };

    return (
        <React.Fragment>
            <Row>
                <Col sm="12">
                    <p>Gateway charts</p>
                    <Button
                        variant="primary"
                        onClick={() => setShowSelectModal(true)}
                    >
                        Select Gateways
                    </Button>

                    <DashboardCreateSelectGatewayModal
                        show={showSelectModal}
                        onHide={() => setShowSelectModal(false)}
                        values={selectedGateways}
                        onSave={onGatewayChange}
                    />
                </Col>
            </Row>

            <Row className="mt-4 cstm-table">
                <Col sm={12}>
                    <Form id="chart-edit-form" onChange={formChange}>
                        {!!Object.values(selectedGateways).length && (
                            <div className="table-head" key="head">
                                <Row className="no-checkbox">
                                    <Col>Gateway</Col>
                                    <Col>Chart Name</Col>
                                    <Col>Chart for</Col>
                                    <Col>Chart Type</Col>
                                    <Col md={{ span: 1 }}></Col>
                                </Row>
                            </div>
                        )}

                        {Object.values(selectedGateways).map((gateway) => (
                            <div
                                key={gateway.gateway_id}
                                id={gateway.gateway_id}
                                className="table-row gateway-chart-info"
                            >
                                <div className="mobile-chart-info">
                                    <Row className="no-checkbox">
                                        <Col>
                                            <Row>
                                                <div className="selected-gateway-header">
                                                    <p>{gateway.name}</p>
                                                    <i
                                                        className="material-icons cursor-pointer text-primary-red-1"
                                                        onClick={() =>
                                                            onRemove(gateway)
                                                        }
                                                        onKeyDown={() =>
                                                            onRemove(gateway)
                                                        }
                                                    >
                                                        close
                                                    </i>
                                                </div>
                                            </Row>
                                            {Object.keys(GatewayChartFor).map(
                                                (key) => {
                                                    const name = `${gateway.gateway_id}.${key}.chart_name`;
                                                    return (
                                                        <Row key={key}>
                                                            <div className="selected-gateway-info">
                                                                <Form.Check
                                                                    id={name}
                                                                    name={name}
                                                                    type="checkbox"
                                                                    defaultChecked={_.get(
                                                                        formValues,
                                                                        name
                                                                    )}
                                                                    label={
                                                                        GatewayChartFor[
                                                                            key as keyof typeof GatewayChartFor
                                                                        ]
                                                                    }
                                                                    custom
                                                                />
                                                                <Form.Group>
                                                                    <Form.Label>
                                                                        Chart
                                                                        Name
                                                                    </Form.Label>
                                                                    <Form.Control
                                                                        name={
                                                                            name
                                                                        }
                                                                        type="text"
                                                                        placeholder="Chart Name"
                                                                        defaultValue={_.get(
                                                                            formValues,
                                                                            name
                                                                        )}
                                                                    />
                                                                </Form.Group>
                                                                <Form.Group>
                                                                    <Form.Label>
                                                                        Chart
                                                                        Type
                                                                    </Form.Label>
                                                                    <Form.Control
                                                                        id={
                                                                            name
                                                                        }
                                                                        name={
                                                                            name
                                                                        }
                                                                        as="select"
                                                                        defaultValue={_.get(
                                                                            formValues,
                                                                            name
                                                                        )}
                                                                        custom
                                                                    >
                                                                        {Object.keys(
                                                                            GateWayChartType
                                                                        ).map(
                                                                            (
                                                                                key
                                                                            ) => (
                                                                                <option
                                                                                    key={
                                                                                        key
                                                                                    }
                                                                                    value={
                                                                                        key
                                                                                    }
                                                                                >
                                                                                    {
                                                                                        GateWayChartType[
                                                                                            key as keyof typeof GateWayChartType
                                                                                        ]
                                                                                    }
                                                                                </option>
                                                                            )
                                                                        )}
                                                                    </Form.Control>
                                                                </Form.Group>
                                                            </div>
                                                        </Row>
                                                    );
                                                }
                                            )}
                                        </Col>
                                    </Row>
                                </div>

                                <div className="desktop-chart-info">
                                    <Row className="no-checkbox">
                                        <Col>{gateway.name}</Col>
                                        <Col>
                                            {Object.keys(GatewayChartFor).map(
                                                (key) => {
                                                    const name = `${gateway.gateway_id}.${key}.chart_name`;
                                                    return (
                                                        <Form.Control
                                                            key={key}
                                                            name={name}
                                                            type="text"
                                                            placeholder="Chart Name"
                                                            defaultValue={_.get(
                                                                formValues,
                                                                name
                                                            )}
                                                        />
                                                    );
                                                }
                                            )}
                                        </Col>
                                        <Col>
                                            {Object.keys(GatewayChartFor).map(
                                                (key) => {
                                                    const name = `${gateway.gateway_id}.${key}.chart_for`;

                                                    return (
                                                        <Form.Check
                                                            key={key}
                                                            id={name}
                                                            name={name}
                                                            type="checkbox"
                                                            defaultChecked={_.get(
                                                                formValues,
                                                                name
                                                            )}
                                                            className="text-break"
                                                            label={
                                                                GatewayChartFor[
                                                                    key as keyof typeof GatewayChartFor
                                                                ]
                                                            }
                                                            custom
                                                        />
                                                    );
                                                }
                                            )}
                                        </Col>
                                        <Col>
                                            {Object.keys(GatewayChartFor).map(
                                                (key) => {
                                                    const name = `${gateway.gateway_id}.${key}.chart_type`;

                                                    return (
                                                        <Form.Control
                                                            key={key}
                                                            id={name}
                                                            name={name}
                                                            custom
                                                            as="select"
                                                            defaultValue={_.get(
                                                                formValues,
                                                                name
                                                            )}
                                                        >
                                                            {Object.keys(
                                                                GateWayChartType
                                                            ).map((key) => (
                                                                <option
                                                                    key={key}
                                                                    value={key}
                                                                >
                                                                    {
                                                                        GateWayChartType[
                                                                            key as keyof typeof GateWayChartType
                                                                        ]
                                                                    }
                                                                </option>
                                                            ))}
                                                        </Form.Control>
                                                    );
                                                }
                                            )}
                                        </Col>
                                        <Col
                                            md={{ span: 1 }}
                                            className="d-flex justify-content-end"
                                        >
                                            <i
                                                className="material-icons cursor-pointer mr-2 text-primary-red-1"
                                                onClick={() =>
                                                    onRemove(gateway)
                                                }
                                                onKeyDown={() =>
                                                    onRemove(gateway)
                                                }
                                            >
                                                close
                                            </i>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        ))}
                    </Form>
                </Col>
            </Row>
            <CreateChartModal
                show={createModalShow && !_.isEmpty(createData)}
                onHide={() => setCreateModalShow(false)}
            />
        </React.Fragment>
    );
};

export default React.memo(DashboardCreateGatewayComponent);
